/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { FiSliders, FiAlertTriangle } from 'react-icons/fi'
import {
  MdOutlineShowChart,
  MdOutlineTimer,
  MdOutlineWifi,
  MdOutlineWifiOff,
  MdSignalWifiConnectedNoInternet0,
  MdThermostat,
  MdWaterDrop,
} from 'react-icons/md'

import { useDashboard } from '@/hooks'
import { SeverityIcon } from '@/pages/assets/map/AssetTable/AlertSeverityIcon'

import { useColdStorage } from './ColdStorageContext'
import { ALERT_WIDTH, DEFAULT_LIST_WIDTH, MIN_LIST_WIDTH, TIMING_WIDTH, columnWidths } from './columns'
import {
  type Units,
  doorLabel,
  formatPercent,
  formatRange,
  formatTemp,
  formatTime,
  formatTiming,
  highestSeverity,
  isOfflineTemperature,
  isStaleReading,
  sensorStatus,
  STALE_READING_CLASS,
} from './format'
import { type ListRow, buildRows, sensorSummary } from './rows'
import type { InStoreAsset } from './types'
import { useNow } from './useNow'

export interface ColdStorageListViewProps {
  locations: InStoreAsset[]
  units: Units
  /** The location whose floor plan is showing. */
  selectedLocationId?: number
  selectedSensorId?: number
  hoveredSensorId?: number
  isLoading?: boolean
  error?: string
  /** Shows the location's floor plan. */
  onSelectLocation: (id: number) => void
  /** Opens the location's detail window. */
  onOpenLocation: (id: number) => void
  onSelectSensor: (id: number) => void
  /** Hovering a sensor row shows its label on the floor plan. */
  onHoverSensor?: (id: number | undefined) => void
  onEditRange: (id: number) => void
  /** Blast freezer: a column for each sensor's time in range, or estimated time to it. */
  showTiming?: boolean
  /** Epoch ms the timing, and the age that fades a stale reading, are measured to. */
  now?: number
}

/** Red outside the range, green inside it, gray with no tag; plain when the location has no range. */
const readingClass = (status: ReturnType<typeof sensorStatus>, hasRange: boolean): string => {
  if (status === 'alarm') return 'text-error font-semibold'
  if (status === 'offline') return 'text-base-content/40'
  return hasRange ? 'text-success' : 'text-base-content'
}

/**
 * Locations as group rows, each followed by a summary row and its sensors: name, temperature, humidity,
 * and on the blast freezer screen the time in range between the two. A temperature
 * outside the location's range reads red; a sensor with no tag, gray. An estimated
 * time to range reads negative and italic.
 * Plain rows rather than a virtualised table — a company has tens of sensors, not
 * thousands, and grouped rows are simpler without one.
 */
export const ColdStorageListView = ({
  locations,
  units,
  selectedLocationId,
  selectedSensorId,
  hoveredSensorId,
  isLoading,
  error,
  onSelectLocation,
  onOpenLocation,
  onSelectSensor,
  onHoverSensor,
  onEditRange,
  showTiming = false,
  now = Date.now(),
}: ColdStorageListViewProps) => {
  const rows = buildRows(locations)
  const selectedRef = useRef<HTMLTableRowElement>(null)
  const [available, setAvailable] = useState(DEFAULT_LIST_WIDTH)
  const scrollEl = useRef<HTMLDivElement | null>(null)
  const observer = useRef<ResizeObserver | null>(null)

  // Column widths follow the panel: clientWidth excludes the vertical scrollbar, so the
  // table never overflows sideways on its account. The observer catches the panel being
  // resized; the layout effect below catches the scrollbar appearing or going, which
  // changes clientWidth without changing the container's own size.
  const measure = useCallback(() => {
    const el = scrollEl.current
    if (el) setAvailable(el.clientWidth)
  }, [])
  // A callback ref, because the scroll container only exists once there are rows to
  // show, after the loading state, and a mount-only effect would have looked too early.
  const scrollRef = useCallback(
    (el: HTMLDivElement | null) => {
      observer.current?.disconnect()
      observer.current = null
      scrollEl.current = el
      if (!el || typeof ResizeObserver === 'undefined') return
      measure()
      observer.current = new ResizeObserver(measure)
      observer.current.observe(el)
    },
    [measure]
  )
  useLayoutEffect(measure, [measure, rows.length])
  const timingWidth = showTiming ? TIMING_WIDTH : 0
  const fixedWidth = timingWidth + ALERT_WIDTH
  const widths = columnWidths(available - fixedWidth)

  // Keep the selection in view when it was made on the floor plan.
  useEffect(() => {
    selectedRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [selectedLocationId, selectedSensorId])

  if (error) {
    return <div className="p-4 text-sm text-error">{error}</div>
  }
  if (isLoading && rows.length === 0) {
    return (
      <div className="p-4 flex items-center gap-2 text-sm text-base-content/60">
        <span className="loading loading-spinner loading-sm" /> Loading locations…
      </div>
    )
  }
  if (rows.length === 0) {
    return <div className="p-4 text-sm text-base-content/60">No locations to show.</div>
  }

  const span = showTiming ? 5 : 4
  const renderRow = (row: ListRow) => {
    if (row.kind === 'location') {
      const { asset } = row
      const selected = asset.id === selectedLocationId
      return (
        <tr
          key={row.key}
          ref={selected ? selectedRef : undefined}
          data-testid={`location-row-${asset.id}`}
          aria-selected={selected}
          title="Show the floor plan"
          onClick={() => onSelectLocation(asset.id)}
          className={`cursor-pointer bg-primary text-primary-content hover:bg-primary/90 ${
            selected ? 'outline outline-2 outline-primary-content -outline-offset-4' : ''
          }`}
        >
          <td className="px-3 py-1.5 font-semibold" colSpan={span}>
            <div className="flex items-center gap-2 min-w-0">
              {asset.gateway ? (
                asset.gateway.online ? (
                  <MdOutlineWifi className="w-4 h-4 text-green-300 shrink-0" title="Gateway online" />
                ) : (
                  <MdOutlineWifiOff className="w-4 h-4 text-primary-content/60 shrink-0" title="Gateway offline" />
                )
              ) : (
                <span className="w-4 h-4 shrink-0" />
              )}
              <span className="flex-1 min-w-0 break-words">{asset.name}</span>
            </div>
          </td>
        </tr>
      )
    }

    if (row.kind === 'summary') {
      const { asset } = row
      return (
        <tr
          key={row.key}
          data-testid={`summary-row-${asset.id}`}
          title="Show every sensor on one graph, and the location's alerts"
          onClick={() => onOpenLocation(asset.id)}
          className="cursor-pointer bg-base-200 hover:bg-base-300"
        >
          <td className="px-3 py-1.5 font-semibold" colSpan={2}>
            {/* The icon sits under the location's gateway icon. */}
            <div className="flex items-center gap-2 min-w-0">
              <MdOutlineShowChart className="w-4 h-4 shrink-0" aria-hidden />
              <span className="truncate">{sensorSummary(asset)}</span>
            </div>
          </td>
          <td className="px-3 py-1.5 text-right whitespace-nowrap" colSpan={span - 2}>
            <button
              type="button"
              className="btn btn-ghost btn-xs gap-1 font-normal -mr-2"
              title="Set the acceptable temperature range"
              aria-label={`Set range for ${asset.name}`}
              onClick={(e) => {
                e.stopPropagation()
                onEditRange(asset.id)
              }}
            >
              <span className="text-xs">{formatRange(asset.threshold, units)}</span>
              <FiSliders className="w-3.5 h-3.5" />
            </button>
          </td>
        </tr>
      )
    }

    const { asset, sensor } = row
    const selected = sensor.id === selectedSensorId
    const status = sensorStatus(sensor, asset.threshold)
    const offline = isOfflineTemperature(sensor)
    const reading =
      sensor.type === 'door' ? (
        doorLabel(sensor)
      ) : offline ? (
        <MdSignalWifiConnectedNoInternet0
          className="w-4 h-4 inline text-base-content/40"
          aria-label="Offline"
          data-testid={`sensor-offline-${sensor.id}`}
        />
      ) : sensor.tagState && sensor.tagState !== 'ok' ? (
        'Fault'
      ) : (
        formatTemp(sensor.temperatureCelsius, units)
      )
    const hovered = sensor.id === hoveredSensorId
    const severity = highestSeverity(sensor.activeAlerts)
    const timing = showTiming && sensor.type === 'temperature' ? formatTiming(sensor.timing, now) : undefined
    // Each reading says when it was taken on hover and fades once it is over an hour old: a
    // tag can keep sending temperature long after it stops sending humidity.
    const showsTemperature = sensor.type === 'temperature' && !offline
    const staleTemperature = showsTemperature && isStaleReading(sensor.sampleTime, now)
    const humidity = sensor.humidity?.enabled ? sensor.humidity : undefined
    const staleHumidity = humidity?.percent != null && isStaleReading(humidity.sampleTime, now)
    return (
      <tr
        key={row.key}
        ref={selected ? selectedRef : undefined}
        data-testid={`sensor-row-${sensor.id}`}
        aria-selected={selected}
        onClick={() => onSelectSensor(sensor.id)}
        onMouseEnter={() => onHoverSensor?.(sensor.id)}
        onMouseLeave={() => onHoverSensor?.(undefined)}
        className={`cursor-pointer ${selected ? 'bg-primary/10' : hovered ? 'bg-base-200/60' : 'hover:bg-base-200/60'}`}
      >
        <td className="pl-9 pr-3 py-1.5 truncate">{sensor.name}</td>
        <td
          className="px-0 py-1.5 text-center"
          data-testid={`sensor-alert-${sensor.id}`}
          title={sensor.activeAlerts.map((alert) => alert.name).join('\n') || undefined}
        >
          {severity && <SeverityIcon severity={severity} />}
        </td>
        <td
          className={`px-3 py-1.5 text-right tabular-nums whitespace-nowrap ${
            offline || sensor.type === 'door' ? '' : readingClass(status, !!asset.threshold)
          } ${staleTemperature ? STALE_READING_CLASS : ''}`}
          title={
            showsTemperature && sensor.sampleTime ? `Temperature reported ${formatTime(sensor.sampleTime)}` : undefined
          }
          data-testid={`sensor-temperature-${sensor.id}`}
        >
          {reading}
        </td>
        {showTiming && (
          <td
            data-testid={`sensor-timing-${sensor.id}`}
            title={timing?.title}
            className={`px-3 py-1.5 text-right tabular-nums whitespace-nowrap ${
              timing?.estimate ? 'italic text-base-content/60' : 'text-base-content/80'
            }`}
          >
            {timing?.text ?? ''}
          </td>
        )}
        <td
          className={`px-3 py-1.5 text-right tabular-nums text-base-content/80 ${staleHumidity ? STALE_READING_CLASS : ''}`}
          title={humidity?.sampleTime ? `Humidity reported ${formatTime(humidity.sampleTime)}` : undefined}
          data-testid={`sensor-humidity-${sensor.id}`}
        >
          {humidity ? formatPercent(humidity.percent) : ''}
        </td>
      </tr>
    )
  }

  // Columns keep a minimum width and the panel scrolls sideways below it, as the asset
  // table does, rather than squeezing the readings into each other.
  return (
    <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto">
      <table
        className="table table-sm table-fixed text-sm"
        style={{
          width: `${widths.name + fixedWidth + widths.temperature + widths.humidity}px`,
          minWidth: `${MIN_LIST_WIDTH + fixedWidth}px`,
        }}
      >
        <colgroup>
          <col style={{ width: `${widths.name}px` }} />
          <col style={{ width: `${ALERT_WIDTH}px` }} />
          <col style={{ width: `${widths.temperature}px` }} />
          {showTiming && <col style={{ width: `${timingWidth}px` }} />}
          <col style={{ width: `${widths.humidity}px` }} />
        </colgroup>
        <thead className="sticky top-0 bg-base-100 z-10">
          <tr>
            <th>Location / Sensor</th>
            <th className="px-0 text-center">
              <span className="tooltip tooltip-left" data-tip="Active alerts">
                <FiAlertTriangle className="w-4 h-4 inline" aria-label="Active alerts" />
              </span>
            </th>
            {/* The tooltips point left, over the table: the tip is an absolutely positioned box
                inside the scroll container, and one reaching past the last column would count as
                scrollable overflow and make the panel scroll sideways. */}
            <th className="text-right">
              <span className="tooltip tooltip-left" data-tip="Temperature">
                <MdThermostat className="w-4 h-4 inline" aria-label="Temperature" />
              </span>
            </th>
            {showTiming && (
              <th className="text-right">
                <span className="tooltip tooltip-left" data-tip="Time in range (negative: estimated time to it)">
                  <MdOutlineTimer className="w-4 h-4 inline" aria-label="Time in range" />
                </span>
              </th>
            )}
            <th className="text-right">
              <span className="tooltip tooltip-left" data-tip="Humidity">
                <MdWaterDrop className="w-4 h-4 inline" aria-label="Humidity" />
              </span>
            </th>
          </tr>
        </thead>
        <tbody>{rows.map(renderRow)}</tbody>
      </table>
    </div>
  )
}

export const ColdStorageList = () => {
  const context = useColdStorage()
  const units = useDashboard().configuration.units as Units
  // Ticks on both screens: the blast freezer's timing counts, and a reading fades as it ages.
  const now = useNow(true)
  return (
    <ColdStorageListView
      locations={context.locations}
      units={units}
      selectedLocationId={context.surfaceLocationId}
      selectedSensorId={context.selectedSensorId}
      hoveredSensorId={context.hoveredSensorId}
      isLoading={context.isLoading}
      error={context.error}
      onSelectLocation={context.selectLocation}
      onOpenLocation={context.openLocation}
      onSelectSensor={context.selectSensor}
      onHoverSensor={context.setHoveredSensorId}
      onEditRange={context.setRangeLocationId}
      showTiming={context.mode.showTiming}
      now={now}
    />
  )
}
